import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  adminApi,
  type TaskAuditItem,
  type TaskAuditFilter,
} from "../../api/endpoints";
import { userDisplayName, userInitials } from "../../lib/userAvatar";

const ACTION_LABEL: Record<TaskAuditItem["action"], string> = {
  create: "Создал задачу",
  update: "Изменил",
  delete: "Удалил задачу",
  move: "Переместил",
  clone: "Дублировал",
  apply: "Применил",
};

const ACTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Все действия" },
  { value: "create", label: "Создание" },
  { value: "update", label: "Изменение" },
  { value: "move", label: "Перемещение" },
  { value: "delete", label: "Удаление" },
];

const FIELD_LABEL: Record<string, string> = {
  title: "Название",
  description: "Описание",
  status: "Статус",
  riskLevel: "Риск",
  plannedDate: "Запланировано на",
  dueDate: "Крайний срок",
  dueTime: "Время крайнего срока",
  assigneeId: "Ответственный (ID)",
  assigneeName: "Ответственный",
  taskTypeId: "Тип задачи",
  launchId: "Запуск",
  position: "Позиция",
};

const PAGE_SIZE = 50;

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days} дн назад`;
  return d.toLocaleDateString("ru-RU");
}

function formatExact(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "medium" });
}

interface FiltersState {
  userId: string;
  action: string;
  from: string;
  to: string;
  q: string;
  taskId: string;
}

const EMPTY_FILTERS: FiltersState = {
  userId: "",
  action: "",
  from: "",
  to: "",
  q: "",
  taskId: "",
};

export function TasksAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Фильтры читаем из URL — это позволяет ссылаться на конкретный срез
  // (например, кнопка «История» в TaskModal даёт URL ?taskId=...).
  const filters: FiltersState = useMemo(
    () => ({
      userId: searchParams.get("userId") ?? "",
      action: searchParams.get("action") ?? "",
      from: searchParams.get("from") ?? "",
      to: searchParams.get("to") ?? "",
      q: searchParams.get("q") ?? "",
      taskId: searchParams.get("taskId") ?? "",
    }),
    [searchParams],
  );

  // Локальное состояние для шапки (q применяется по Enter / blur).
  const [draft, setDraft] = useState<FiltersState>(filters);

  // Список юзеров для дропдауна «Кто».
  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await adminApi.listUsers()).users,
    staleTime: 60_000,
  });

  const auditQuery = useInfiniteQuery({
    queryKey: ["admin-tasks-audit", filters],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params: TaskAuditFilter = {
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        q: filters.q || undefined,
        taskId: filters.taskId || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam ?? undefined,
      };
      return adminApi.taskAudit(params);
    },
    getNextPageParam: (last) => last.nextCursor,
  });

  const items = useMemo(
    () => (auditQuery.data?.pages ?? []).flatMap((p) => p.items),
    [auditQuery.data],
  );

  const applyFilters = (next: FiltersState) => {
    const usp = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v) usp.set(k, v);
    });
    setSearchParams(usp, { replace: false });
  };

  const onApply = () => applyFilters(draft);
  const onReset = () => {
    setDraft(EMPTY_FILTERS);
    applyFilters(EMPTY_FILTERS);
  };

  const hasActiveFilter =
    !!(filters.userId || filters.action || filters.from || filters.to || filters.q || filters.taskId);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Лог действий по задачам</h1>
          <div className="admin-sub">
            Кто создавал, менял, перемещал и удалял задачи. История пишется навсегда —
            используй фильтры, чтобы найти конкретное действие.
          </div>
        </div>
      </div>

      <div className="audit-filters">
        <div className="audit-filter-row">
          <label className="audit-filter-field">
            <span>Кто</span>
            <select
              value={draft.userId}
              onChange={(e) => setDraft({ ...draft, userId: e.target.value })}
            >
              <option value="">Все</option>
              {(usersQuery.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {userDisplayName(u.name, u.email)} · {u.role}
                </option>
              ))}
            </select>
          </label>

          <label className="audit-filter-field">
            <span>Действие</span>
            <select
              value={draft.action}
              onChange={(e) => setDraft({ ...draft, action: e.target.value })}
            >
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="audit-filter-field">
            <span>С</span>
            <input
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </label>

          <label className="audit-filter-field">
            <span>По</span>
            <input
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </label>
        </div>

        <div className="audit-filter-row">
          <label className="audit-filter-field audit-filter-field-grow">
            <span>Поиск по названию задачи</span>
            <input
              type="text"
              placeholder="часть названия задачи…"
              value={draft.q}
              onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onApply();
                }
              }}
            />
          </label>

          <div className="audit-filter-actions">
            <button type="button" className="btn-primary" onClick={onApply}>
              Применить
            </button>
            {hasActiveFilter && (
              <button type="button" className="btn-ghost" onClick={onReset}>
                Сбросить
              </button>
            )}
          </div>
        </div>

        {filters.taskId && (
          <div className="audit-filter-pill">
            Фильтр по задаче: <code>{filters.taskId}</code>{" "}
            <button
              type="button"
              className="audit-filter-pill-x"
              onClick={() => {
                const next = { ...filters, taskId: "" };
                setDraft(next);
                applyFilters(next);
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {auditQuery.isLoading && <div className="admin-empty">Загружаем…</div>}
      {auditQuery.isError && (
        <div className="admin-empty admin-error">
          Не удалось загрузить лог. {String(auditQuery.error)}
        </div>
      )}
      {!auditQuery.isLoading && items.length === 0 && (
        <div className="admin-empty">
          {hasActiveFilter
            ? "Под фильтр ничего не попало. Попробуйте расширить условия."
            : "Действий пока не было."}
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="audit-list">
            {items.map((item) => (
              <AuditRow key={item.id} item={item} />
            ))}
          </div>

          <div className="audit-load-more-wrap">
            {auditQuery.hasNextPage ? (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => auditQuery.fetchNextPage()}
                disabled={auditQuery.isFetchingNextPage}
              >
                {auditQuery.isFetchingNextPage ? "Загружаем…" : "Загрузить ещё"}
              </button>
            ) : (
              <div className="audit-end">Это все записи</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AuditRow({ item }: { item: TaskAuditItem }) {
  const [expanded, setExpanded] = useState(false);
  const actorName = item.actor
    ? userDisplayName(item.actor.name, item.actor.email)
    : "Пользователь удалён";
  const initials = item.actor ? userInitials(item.actor.name, item.actor.email) : "?";
  const taskLabel = item.taskTitle ?? `${item.taskId} (задача удалена)`;

  const diffEntries = parseDiff(item.diff);
  const hasDetails = diffEntries.length > 0;

  return (
    <div className="audit-row">
      <button
        type="button"
        className="audit-row-head"
        onClick={() => hasDetails && setExpanded((v) => !v)}
        disabled={!hasDetails}
        title={hasDetails ? (expanded ? "Свернуть" : "Раскрыть детали") : undefined}
      >
        {item.actor?.picture ? (
          <img
            className="audit-avatar"
            src={item.actor.picture}
            alt={actorName}
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="audit-avatar audit-avatar-fallback">{initials}</div>
        )}
        <div className="audit-row-main">
          <div className="audit-row-title">
            <b>{actorName}</b>{" "}
            <span className="audit-action">{ACTION_LABEL[item.action] ?? item.action}</span>{" "}
            <span className="audit-task-title">«{taskLabel}»</span>
          </div>
          <div className="audit-row-meta">
            <span title={formatExact(item.changedAt)}>{formatRelative(item.changedAt)}</span>
            {item.actor?.role && (
              <span className={"admin-role-tag role-" + item.actor.role}>
                {item.actor.role}
              </span>
            )}
            {hasDetails && (
              <span className="audit-row-toggle">{expanded ? "Скрыть детали" : "Подробнее"}</span>
            )}
          </div>
        </div>
      </button>

      {expanded && hasDetails && (
        <div className="audit-row-diff">
          {diffEntries.map((e) => (
            <div key={e.field} className="audit-diff-row">
              <div className="audit-diff-field">{FIELD_LABEL[e.field] ?? e.field}</div>
              <div className="audit-diff-value">{stringifyValue(e.value)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface DiffEntry {
  field: string;
  value: unknown;
}

function parseDiff(raw: unknown): DiffEntry[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>)
    .filter(([key]) => !key.startsWith("__"))
    .map(([field, value]) => ({ field, value }));
}

function stringifyValue(v: unknown): string {
  if (v === null) return "—";
  if (v === undefined) return "—";
  if (typeof v === "string") return v.length > 0 ? v : "пусто";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v, null, 2);
}
